import { openDB, DBSchema } from 'idb';
import { API_CONFIG } from '../../config/api.config';

interface TTSCacheDB extends DBSchema {
    audio: {
        key: string;
        value: {
            audioBlob: Blob;
            timestamp: number;
        };
    };
}

const DB_NAME = 'tts-cache';
const STORE_NAME = 'audio';

export class CloudTTSService {
    private static instance: CloudTTSService;
    private dbPromise = openDB<TTSCacheDB>(DB_NAME, 1, {
        upgrade(db) {
            db.createObjectStore(STORE_NAME);
        },
    });

    private constructor() {
        // No local API key needed when using proxy
    }

    public static getInstance(): CloudTTSService {
        if (!CloudTTSService.instance) {
            CloudTTSService.instance = new CloudTTSService();
        }
        return CloudTTSService.instance;
    }

    private pendingRequests: Map<string, Promise<Blob | null>> = new Map();

    /**
     * Quickly checks if audio is already in cache.
     */
    public async getCachedAudio(text: string, voice: string = 'alloy', speed: number = 1.0): Promise<Blob | null> {
        const cacheKey = `${text}-${voice}-${speed}`;
        try {
            const db = await this.dbPromise;
            const cached = await db.get(STORE_NAME, cacheKey);
            if (cached) return cached.audioBlob;
        } catch (error) {
            console.error('❌ CloudTTSService: Cache lookup error', error);
        }
        return null;
    }

    /**
     * Gets a direct URL for streaming audio.
     * Returns null for long text (> 1000 chars) to avoid URL length issues.
     */
    public getAudioUrl(text: string, voice: string = 'alloy', speed: number = 1.0): string | null {
        if (text.length > 1000) return null;
        
        const params = new URLSearchParams({
            text,
            voice,
            speed: speed.toString()
        });
        
        // Use the Python backend API base URL
        const apiBase = API_CONFIG.python.apiUrl;
        return `${apiBase}/ai/tts?${params.toString()}`;
    }

    public async getAudio(text: string, voice: 'alloy' | 'echo' | 'fable' | 'onyx' | 'nova' | 'shimmer' = 'alloy', speed: number = 1.0): Promise<Blob | null> {
        // Use the correct TTS endpoint on Python backend
        const ttsUrl = `${API_CONFIG.python.apiUrl}/ai/tts`;

        const cacheKey = `${text}-${voice}-${speed}`;

        // 1. Check Cache
        const cachedBlob = await this.getCachedAudio(text, voice, speed);
        if (cachedBlob) {
            console.log('✅ CloudTTSService: Cache hit');
            return cachedBlob;
        }

        // 2. Check Pending Requests (Deduplication)
        if (this.pendingRequests.has(cacheKey)) {
            console.log('⏳ CloudTTSService: Joining pending request for', text.substring(0, 20) + '...');
            return this.pendingRequests.get(cacheKey)!;
        }

        // 3. Fetch from API
        const fetchPromise = (async () => {
            try {
                console.log(`☁️ CloudTTSService: Fetching from (${ttsUrl})...`);

                const response = await fetch(ttsUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        model: 'tts-1',
                        input: text,
                        voice: voice,
                        speed: speed
                    }),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error(`❌ CloudTTSService: API Error ${response.status}: ${errorText}`);
                    return null;
                }

                const audioBlob = await response.blob();

                // 4. Save to Cache
                try {
                    const db = await this.dbPromise;
                    await db.put(STORE_NAME, {
                        audioBlob,
                        timestamp: Date.now(),
                    }, cacheKey);
                    console.log('💾 CloudTTSService: Cached for future use');
                } catch (error) {
                    console.error('❌ CloudTTSService: Cache write error', error);
                }

                return audioBlob;
            } catch (error) {
                console.error('❌ CloudTTSService: Network error', error);
                return null;
            } finally {
                this.pendingRequests.delete(cacheKey);
            }
        })();

        this.pendingRequests.set(cacheKey, fetchPromise);
        return fetchPromise;
    }

    public async clearCache(): Promise<void> {
        const db = await this.dbPromise;
        await db.clear(STORE_NAME);
        console.log('🗑️ CloudTTSService: Cache cleared');
    }
}

export const cloudTTSService = CloudTTSService.getInstance();
